import { IconButton, Menu, MenuItem, Stack } from "@mui/material";
import EditIcon from "src/assets/svg-icons/aggregators/edit.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { notificationsState } from "src/store/notifications";
import {
  useDeleteSalaryEmployee,
  SalaryEmployeeListRow,
} from "src/api/salary/employees";
import { APIErrorResponse } from "src/types/generic";
import MenuItemLabel from "src/pages/salary/menu-item-label";
import AttachmentsModal from "../../attachments-popup";

interface IAction {
  row: SalaryEmployeeListRow;
  refetch: () => void;
}

const TableActions = ({ row, refetch }: IAction) => {
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const { mutate: deleteEmployee, isPending: deleting } = useDeleteSalaryEmployee();

  const closeMenu = () => setAnchorEl(null);

  const onDelete = () => {
    deleteEmployee(row.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        refetch();
        setNotifications([
          ...notifications,
          { type: "success", message: generalT("deletedSuccessfully") },
        ]);
      },
      onError: (err: APIErrorResponse) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  return (
    <>
      <Stack direction="row" justifyContent="center" alignItems="center">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setAttachOpen(true);
          }}
          sx={{ gap: 1 }}
        >
          <img src={DownloadIcon} alt="" />
          <MenuItemLabel>
            {generalT("attachments")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(`/salary/employees/${row.id}/edit`);
          }}
          sx={{ gap: 1 }}
        >
          <img src={EditIcon} alt="" />
          <MenuItemLabel>
            {generalT("edit")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setConfirmDelete(true);
          }}
          sx={{ gap: 1 }}
        >
          <img src={DeleteIcon} alt="" />
          <MenuItemLabel>
            {generalT("delete")}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate(`/salary/employees/${row.id}`);
          }}
          sx={{ gap: 1 }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
          <MenuItemLabel>
            {generalT("details")}
          </MenuItemLabel>
        </MenuItem>
      </Menu>

      <ConfirmPopup
        open={confirmDelete}
        handleClose={() => setConfirmDelete(false)}
        handleConfirm={onDelete}
        isPending={deleting}
        item={row.name}
      />
      <AttachmentsModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        employeeId={row.id}
        employeeName={row.name}
      />
    </>
  );
};

export default TableActions;
